import React, { useEffect, useRef, useState } from 'react';
import styles from './GraphicDesignCreativeSkills.module.css';

const GraphicDesignCreativeSkills = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const sectionRef = useRef(null);

  const skills = [
    { name: 'Photoshop', percentage: 95, color: '#31A8FF' },
    { name: 'Illustrator', percentage: 90, color: '#FF9A00' },
    { name: 'After Effects', percentage: 85, color: '#9999FF' },
    { name: 'Figma', percentage: 88, color: '#F24E1E' },
    { name: 'Procreate', percentage: 92, color: '#FF6B6B' },
    { name: 'Animate', percentage: 80, color: '#FF3366' },
    { name: 'Canva', percentage: 93, color: '#00C4CC' },
    { name: 'Adobe XD', percentage: 87, color: '#FF61F6' },
    { name: 'CorelDRAW', percentage: 82, color: '#00A85A' },
    { name: 'Premiere Pro', percentage: 86, color: '#9999FF' },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section id="skills" className={styles.skillsSection} ref={sectionRef}>
      <div className={styles.container}>
        <h2 className={`${styles.sectionTitle} ${isVisible ? styles.fadeInUp : ''}`}>
          Creative Skills
        </h2>
        <p className={`${styles.skillsText} ${isVisible ? styles.fadeInUp : ''}`}>
          Mastering the art of visual storytelling
        </p>
        
        <div className={styles.skillsGrid}>
          {skills.map((skill, index) => (
            <div 
              key={index} 
              className={`${styles.skillItem} ${isVisible ? styles.fadeInScale : ''}`}
              style={{ animationDelay: `${index * 0.1}s` }}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <div className={styles.skillHeader}>
                <span className={styles.skillName}>{skill.name}</span>
                <span className={styles.skillPercentage}>{skill.percentage}%</span>
              </div>
              <div className={styles.skillBarBg}>
                {hoveredIndex === index ? (
                  <div 
                    className={`${styles.skillBarFill} ${styles.fillAnimation}`}
                    style={{
                      width: `${skill.percentage}%`,
                      background: `linear-gradient(90deg, ${skill.color}, ${skill.color}88)`,
                      boxShadow: `0 0 20px ${skill.color}44`,
                      animation: `${styles.fillBar} 1.2s ease-out forwards`
                    }}
                  >
                    <div 
                      className={styles.skillBarShine}
                      style={{ background: `linear-gradient(90deg, transparent, ${skill.color}66, transparent)` }}
                    ></div>
                  </div>
                ) : (
                  <div 
                    className={`${styles.skillBarFill} ${isVisible ? styles.fillAnimation : ''}`}
                    style={{
                      width: isVisible ? `${skill.percentage}%` : '0%',
                      background: `linear-gradient(90deg, ${skill.color}, ${skill.color}88)`,
                      boxShadow: `0 0 20px ${skill.color}44`,
                      transitionDelay: `${index * 0.1}s`
                    }}
                  >
                    <div 
                      className={styles.skillBarShine}
                      style={{ background: `linear-gradient(90deg, transparent, ${skill.color}66, transparent)` }}
                    ></div>
                  </div>
                )}
              </div>
              <div 
                className={styles.skillGlow}
                style={{ background: `radial-gradient(circle at center, ${skill.color}22, transparent)` }}
              ></div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Animated background particles */}
      <div className={styles.particlesContainer}>
        {[...Array(20)].map((_, i) => (
          <div 
            key={i} 
            className={styles.particle}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${5 + Math.random() * 10}s`
            }}
          ></div>
        ))}
      </div>
    </section>
  );
};

export default GraphicDesignCreativeSkills;